import { useEffect, useState } from 'react'
import { FiPlay } from 'react-icons/fi'
import VideoUrlFrame from './VideoUrlFrame'
import WebsiteUrlCard from './WebsiteUrlCard'

const SLIDE_INTERVAL_MS = 5000

function isUploadedSpecialMediaVideo(media) {
  if (!media || media.type !== 'video') return false
  return media.source === 'upload'
    || media.source === 'uploaded'
    || media.resourceType === 'video'
    || Boolean(media.publicId)
}

export function SpecialMediaVideoPreview({ media, className = '', onOpen }) {
  const [generatedPoster, setGeneratedPoster] = useState('')
  const [posterFailed, setPosterFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    const configuredPoster = media?.poster || media?.thumbnail || ''
    if (!media?.url || configuredPoster === media.url) {
      setGeneratedPoster('')
      return undefined
    }
    if (configuredPoster) {
      setGeneratedPoster(configuredPoster)
      setPosterFailed(false)
      return undefined
    }

    const video = document.createElement('video')
    video.crossOrigin = 'anonymous'
    video.preload = 'metadata'
    video.muted = true
    video.playsInline = true
    const timeoutId = window.setTimeout(() => {
      if (!cancelled) setPosterFailed(true)
    }, 8000)

    const extractFrame = () => {
      try {
        const width = Math.max(320, Math.min(video.videoWidth || 640, 960))
        const height = Math.max(180, Math.round(width * ((video.videoHeight || 360) / (video.videoWidth || 640))))
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        canvas.getContext('2d')?.drawImage(video, 0, 0, width, height)
        const frame = canvas.toDataURL('image/jpeg', 0.82)
        if (!cancelled && frame) setGeneratedPoster(frame)
      } catch {
        if (!cancelled) setPosterFailed(true)
      }
    }

    video.onloadedmetadata = () => {
      try {
        video.currentTime = Number.isFinite(video.duration) && video.duration > 0 ? Math.min(video.duration * 0.15, 1) : 0
      } catch {
        setPosterFailed(true)
      }
    }
    video.onseeked = extractFrame
    video.onloadeddata = extractFrame
    video.onerror = () => { if (!cancelled) setPosterFailed(true) }
    video.src = media.url
    video.load()

    return () => {
      cancelled = true
      window.clearTimeout(timeoutId)
      video.pause()
      video.removeAttribute('src')
      video.load()
    }
  }, [media?.url, media?.poster, media?.thumbnail])

  if (!media?.url) return null

  const poster = media.poster && media.poster !== media.url
    ? media.poster
    : media.thumbnail && media.thumbnail !== media.url
      ? media.thumbnail
      : generatedPoster
  const openPreview = (event) => {
    event.preventDefault()
    event.stopPropagation()
    onOpen?.(media)
  }

  return (
    <button
      type="button"
      onClick={openPreview}
      className={`group relative block overflow-hidden bg-black ${className}`}
      aria-label="Play video"
    >
      {poster ? (
        <img src={poster} alt={media.alt || 'Video preview'} className="h-full w-full object-contain" />
      ) : !posterFailed ? (
        <video src={media.url} muted playsInline preload="metadata" className="h-full w-full object-contain" />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gray-900 text-sm text-white/75">Video preview unavailable</div>
      )}
      <span className="absolute inset-0 flex items-center justify-center bg-black/20 transition group-hover:bg-black/35">
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-black/75 text-white shadow-lg">
          <FiPlay className="ml-1 h-7 w-7" />
        </span>
      </span>
    </button>
  )
}

export default function SpecialMediaImageCarousel({ images, getImageStyle, getWrapperStyle, display, onLocalVideoOpen }) {
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    setCurrentIndex((index) => Math.min(index, Math.max(images.length - 1, 0)))
  }, [images.length])

  useEffect(() => {
    if (images.length <= 1) return undefined

    const intervalId = window.setInterval(() => {
      setCurrentIndex((index) => (index + 1) % images.length)
    }, SLIDE_INTERVAL_MS)

    return () => window.clearInterval(intervalId)
  }, [images.length])

  if (!images.length) return null

  const activeImage = images[currentIndex]
  const imageStyle = getImageStyle(activeImage)
  const renderActiveMedia = () => {
    if (activeImage.type === 'video') {
      const isUploadedVideo = isUploadedSpecialMediaVideo(activeImage)
      return !isUploadedVideo && (activeImage.embedUrl || activeImage.source === 'url') ? (
        <VideoUrlFrame video={activeImage} className="w-full h-full" />
      ) : (
        <SpecialMediaVideoPreview
          media={activeImage}
          onOpen={onLocalVideoOpen}
          className="h-full min-h-[180px] w-full"
        />
      )
    }

    if (activeImage.type === 'link') {
      return <WebsiteUrlCard url={activeImage.url} className="w-full h-full" />
    }

    return activeImage.url ? (
      <img
        src={activeImage.url}
        alt={activeImage.alt || ''}
        style={imageStyle}
        className={display === 'full' ? undefined : 'rounded'}
        onError={(event) => {
          event.currentTarget.style.display = 'none'
          event.currentTarget.parentElement?.classList.add('bg-gray-100')
        }}
      />
    ) : (
      <div className="flex min-h-[180px] items-center justify-center bg-gray-100 text-sm text-gray-500">Media unavailable</div>
    )
  }

  if (display === 'full') {
    return (
      <div
        className="relative overflow-hidden"
        style={getWrapperStyle(activeImage)}
        data-special-media-carousel={images.length > 1 ? 'true' : 'false'}
        aria-live="polite"
      >
        {renderActiveMedia()}
      </div>
    )
  }

  return (
    <div
      className="mb-4 relative w-full overflow-hidden"
      data-special-media-carousel={images.length > 1 ? 'true' : 'false'}
      aria-live="polite"
    >
      {renderActiveMedia()}
    </div>
  )
}
